import type { Vec2, Vec3 } from '../../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import { beamSolid, orientedFace, planPoint } from '../../solids';

/** A round clock dial standing `thickness` proud of a wall face, as the 1544 woodcut shows on the town hall tower. */
export interface DialSpec {
  /** A plan point on the wall face. */
  readonly at: Vec2;
  readonly y: number;
  readonly radius: number;
  /** The wall face's outward plan direction. */
  readonly outward: Vec2;
}

const DIAL = { segments: 16, thickness: 0.12 } as const;

export function clockDial(
  builder: MeshBuilder,
  styles: { readonly face: VertexStyle; readonly rim: VertexStyle },
  frame: Frame,
  d: DialSpec
): void {
  const n: Vec2 = d.outward;
  const along: Vec2 = [-n[1], n[0]];
  const ring = (depth: number): Vec3[] =>
    Array.from({ length: DIAL.segments }, (_, k) => {
      const a = (2 * Math.PI * k) / DIAL.segments;
      const s = Math.cos(a) * d.radius;
      return planPoint(
        d.at[0] + along[0] * s + n[0] * depth,
        d.y + Math.sin(a) * d.radius,
        d.at[1] + along[1] * s + n[1] * depth
      );
    });
  const back = ring(0);
  const front = ring(DIAL.thickness);
  const out: Vec3 = [n[0], 0, -n[1]];
  orientedFace(builder, styles.face, frame, front, out);
  orientedFace(builder, styles.rim, frame, back, [-out[0], 0, -out[2]]);
  for (let k = 0; k < DIAL.segments; k++) {
    const j = (k + 1) % DIAL.segments;
    const a = (2 * Math.PI * (k + 0.5)) / DIAL.segments;
    const side: Vec3 = [along[0] * Math.cos(a), Math.sin(a), -along[1] * Math.cos(a)];
    orientedFace(builder, styles.rim, frame, [back[k]!, back[j]!, front[j]!, front[k]!], side);
  }
  // Physical hour marks and one hand; their size and displayed hour are modelling choices.
  const at = (angle: number, radius: number): Vec3 =>
    planPoint(
      d.at[0] + along[0] * Math.cos(angle) * radius + n[0] * (DIAL.thickness + 0.025),
      d.y + Math.sin(angle) * radius,
      d.at[1] + along[1] * Math.cos(angle) * radius + n[1] * (DIAL.thickness + 0.025)
    );
  for (let hour = 0; hour < 12; hour++) {
    const angle = (hour * Math.PI) / 6;
    beamSolid(
      builder,
      styles.rim,
      frame,
      at(angle, d.radius * 0.78),
      at(angle, d.radius * 0.92),
      0.018
    );
  }
  beamSolid(builder, styles.rim, frame, at(2.25, 0), at(2.25, d.radius * 0.63), 0.025);
}
